/*
Initially auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.2.16 public/models/alex/alex.glb -o src/components/TalkingHead/TalkingHeadAvatar.tsx -r public --types
*/

import { AnimationAction, MathUtils, SkinnedMesh, Group, LoopRepeat } from 'three'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useAnimations } from '@react-three/drei'
import { TalkingHeadContext, TalkingHeadContextType } from '../../state/TalkingHeadProvider'
import {
  AnimationsEnum,
  AnimationsListeningArray,
  AnimationsTalkingArray,
  AnimationsThinkingArray,
  AnimationsWaitingArray,
  blinkDuration,
  blinkTick,
  defaultNoAnimationOption,
  // ModelNamesEnum,
  visemesSettings
} from '../../constants/talkingHead'
// TODO: add more controls for blendshapes
import { useControls } from 'leva'
// TODO: return back with animations
import { useFrame } from '@react-three/fiber'
import { useDebug } from '../../hooks/useDebug'
import { AppStateContext } from '../../state/AppProvider'
import { getRandomAnimation } from '../../utils/getAnimation'

export function TalkingHeadAvatar({ avatarRef, model, ...props }: any) {
  const { nodes, scene, animations } = model

  // State
  const appStateContext = useContext(AppStateContext)
  const {
    animation: {
      animation,
      rotateIdle,
      rotateTalk,
      backToIdle,
      fadeInDelay: animationFadeInDelay,
      fadeOutDelay: animationFadeOutDelay,
      startFrame: animationStartFrame,
      setAnimation,
    },
    activeAnimation: { activeAnimation },
    animationCicle: { cicleCount, updateCicleCount },
    modelAnimations: { updateModelAnimations },
    visemes: { visemes },
    talking: { isTalking, talkStartedAt }
  } = useContext(TalkingHeadContext) as TalkingHeadContextType
  const isMounted = useRef<boolean>(false)

  // Debugging
  const debug = useDebug({ debugMode: appStateContext?.state.frontendSettings?.debug })

  // Animations
  // Get model animations
  const modelAnimations = useMemo(() => animations.map((animation: any) => animation.name), [animations])

  // Create controllers for current animations
  const [{ selectedAnimation }, set] = useControls('Available animations', () => ({
    selectedAnimation: {
      value: modelAnimations[0],
      options: [ defaultNoAnimationOption, ...modelAnimations]
    },
  }))

  const setSelectedAnimation = (newAnimation: AnimationsEnum | string) => {
    set({ selectedAnimation: newAnimation })
  }
  const { actions, mixer } = useAnimations(animations, avatarRef)
  const localAvatarRef = useRef<Group>();
  const previousActionRef = useRef<AnimationAction | null>(null)
  const currentActionRef = useRef<AnimationAction | null>(null);

  const handleLoop = (event: THREE.Event) => {
    const current = currentActionRef.current;
    if (event.type === 'loop' && event.action === current) {
      updateCicleCount((prev: number) => prev + 1);
    }
  };

  // Initialization & garbage collection
  useEffect(() => {
    if (avatarRef) avatarRef.current = localAvatarRef.current;
    mixer.addEventListener('loop', handleLoop);

    return () => {
      console.log("destruct")
      mixer.removeEventListener('loop', handleLoop);
      mixer.stopAllAction()
      if (localAvatarRef.current) mixer.uncacheRoot(localAvatarRef.current);
    }
  }, [model]);

  useEffect(() => {
    updateModelAnimations([defaultNoAnimationOption, ...modelAnimations]);
  }, [modelAnimations]);

  // Update current model animation
  useEffect(() => {
    if (animations && selectedAnimation && Object.values(AnimationsEnum).includes(selectedAnimation as AnimationsEnum)) {
      setAnimation(selectedAnimation as AnimationsEnum)
    }
    const currentAction = actions[selectedAnimation as AnimationsEnum] as AnimationAction | undefined;
    currentActionRef.current = currentAction || null;
    const previousAction = previousActionRef.current;

    if (!currentAction) {
      return;
    }

    currentAction.reset().play();

    if (previousAction && previousAction !== currentAction) {
      debug(() => console.log("Crossfading to new animation"))
      previousAction.crossFadeTo(currentAction, 0.5, false);
    } else {
      currentAction.fadeIn(animationFadeInDelay);
    }

    previousActionRef.current = currentAction;

    return () => {
      if (currentAction) {
        currentAction.fadeOut(animationFadeOutDelay);
      }
    }
  }, [selectedAnimation]);

  useEffect(() => {
    if (activeAnimation) {
      setSelectedAnimation(modelAnimations.includes(activeAnimation) ? activeAnimation as any : defaultNoAnimationOption)
    }

  }, [activeAnimation]);

  useEffect(() => {
    if (cicleCount === 0) return;
    let newAnimation = AnimationsEnum.Idle3;
    if (isTalking) {
      newAnimation = getRandomAnimation(AnimationsTalkingArray);
    } else if (AnimationsListeningArray.includes(selectedAnimation as AnimationsEnum) && cicleCount < 5) {
      newAnimation = getRandomAnimation(AnimationsListeningArray);
    } else if (AnimationsThinkingArray.includes(selectedAnimation as AnimationsEnum) && cicleCount < 5) {
      newAnimation = getRandomAnimation(AnimationsThinkingArray);
    } else {
      newAnimation = getRandomAnimation(AnimationsWaitingArray);
      updateCicleCount(0);
    }
    setSelectedAnimation(newAnimation)
  }, [cicleCount]);

  useEffect(() => {
    if (animations) {
      setSelectedAnimation(modelAnimations.includes(animation) ? animation as any : defaultNoAnimationOption)
    }
  }, [animation])

  useEffect(() => {
    mixer.setTime(animationStartFrame)
    isMounted.current = true
  }, []);

  // TODO: add more controls for blendshapes
  // Leva controls
  const controls = Object.values(nodes).reduce((fields: any, node) => {
    if (node instanceof SkinnedMesh && node.morphTargetDictionary) {
      fields[node.name] = Object.assign(
        {},
        ...Object.keys(node.morphTargetDictionary || []).map((key) => {
          return {
            [key]: {
              label: key,
              value: 0,
              min: 0,
              max: 1,
              onChange: (value: any) => smoothlySetMorphValue(key, value, 1)
            }
          }
        })
      );
    }

    return fields;
  }, {}) as Record<string, any>;

  const controlsArray = Object.entries(controls);

  // Use controls outside the loop
  controlsArray.forEach(([name, control]) => useControls(name, control));

  // Smooth morph value transitions
  const smoothlySetMorphValue = (key: any, value: any, speed = 0.1) => {
    scene.traverse((child: any) => {
      if (child.isSkinnedMesh && child.morphTargetDictionary) {
        const index = child.morphTargetDictionary[key]
        if (index === undefined || child.morphTargetInfluences[index] === undefined) return

        child.morphTargetInfluences[index] = MathUtils.lerp(child.morphTargetInfluences[index], value, speed)
      }
    })
  }

  // Eyes blinking
  const blinkingRef = useRef<NodeJS.Timeout>()
  const hasEyesClosed = useRef<boolean>(false)
  const setEyesClosed = (value: boolean) => {
    hasEyesClosed.current = value
  }

  useEffect(() => {
    blinkingRef.current = setInterval(() => {
      setEyesClosed(true)

      setTimeout(() => setEyesClosed(false), blinkDuration)
    }, blinkTick)

    return () => {
      clearTimeout(blinkingRef.current)
      setEyesClosed(false)
    }
  }, [])

  // TODO: return back with blendshapes or animations
  // Rendering loop
  useFrame(() => {
    const uniqueUsedVisemes = [...new Set(visemesSettings.map((item) => item.name))]
    uniqueUsedVisemes.forEach((visemeName: string) => smoothlySetMorphValue(visemeName, 0, 0.1))

    if (talkStartedAt && visemes.length) {
      const now = Date.now()
      const audioDuration = now - talkStartedAt

      visemes.find((viseme) => {
        const visemeSettings = visemesSettings[viseme.visemeId]

        if (viseme.offset >= audioDuration) {
          smoothlySetMorphValue(visemeSettings.name, visemeSettings.morphValue, visemeSettings.speed)
          return true
        } else {
          return false
        }
      })
    }
  })

  return (
    <group {...props} dispose={null} ref={localAvatarRef}>
      <primitive object={scene} />
    </group>
  )
}
